import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ErrorMessageResponse } from '../models/data-response';
import { DataTaskService } from './data-task.service';
import { NavigationHistoryService } from './navigation-history.service';

/**
 * Owns persisted pinned states independently of the active router.
 *
 * This service owns datatasks 3261-3263, the current pin list, title
 * synchronization, and the protected URLs supplied to navigation history.
 * It stores URLs only and never performs navigation.
 */
@Injectable({ providedIn: 'root' })
export class PinnedStateService {
    /** Current persisted pins in display order. */
    public readonly pins$ = new BehaviorSubject<PinnedState[]>([]);

    private readonly titleUpdates = new Set<string>();

    constructor(
        private readonly datatask: DataTaskService,
        private readonly history: NavigationHistoryService,
    ) {}

    /**
     * Loads the user's pins.
     *
     * During initial application load, `removeUnvisitedStartupEntries` removes
     * an automatically recorded pinned URL until the user explicitly visits it.
     */
    public async load(removeUnvisitedStartupEntries = false): Promise<PinnedState[]> {
        const pins = [...(await this.getPinnedStates())].reverse();

        this.setPins(pins);
        if (removeUnvisitedStartupEntries) this.history.removeUnvisitedStartupEntries();

        return pins;
    }

    /** Finds a pin by its exact persisted URL. */
    public findByUrl(url: string): PinnedState | undefined {
        return this.pins$.value.find((pin) => pin.pinned_url === url);
    }

    /**
     * Persists a state as a pin and reloads the canonical backend list.
     * The in-memory list remains unchanged when the request fails.
     */
    public async pin(state: { path: string; title: string }): Promise<PinnedState | null> {
        try {
            const response = await this.savePinnedState({
                pinned_name: state.title,
                pinned_url: state.path,
            });

            if (`${response?.errorcode}` !== '0') return null;

            await this.load();
            return this.findByUrl(state.path) ?? null;
        } catch {
            return null;
        }
    }

    /**
     * Removes a persisted pin after backend confirmation.
     * The in-memory list remains unchanged when the request fails.
     */
    public async unpin(pin: PinnedState): Promise<boolean> {
        try {
            const response = await this.deletePinnedState({
                webpagepinned_keyno: pin.webpagepinned_keyno,
            });

            if (`${response?.errorcode}` !== '0') return false;

            this.setPins(this.pins$.value.filter((item) => item.webpagepinned_keyno !== pin.webpagepinned_keyno));

            return true;
        } catch {
            return false;
        }
    }

    /**
     * Aligns a pin with the resolved title of its visited page.
     * The optimistic title is restored when persistence fails.
     */
    public async syncTitle(state: { path: string; title: string }): Promise<void> {
        const pin = this.findByUrl(state.path);
        const newTitle = state.title?.trim();

        if (!pin || !newTitle || newTitle === '...' || pin.pinned_name === newTitle || this.titleUpdates.has(pin.webpagepinned_keyno)) {
            return;
        }

        const oldTitle = pin.pinned_name;
        this.setPins(this.pins$.value.map((item) => (item.webpagepinned_keyno === pin.webpagepinned_keyno ? { ...item, pinned_name: newTitle } : item)));
        this.titleUpdates.add(pin.webpagepinned_keyno);

        try {
            const response = await this.savePinnedState({
                webpagepinned_keyno: pin.webpagepinned_keyno,
                pinned_name: newTitle,
                pinned_url: pin.pinned_url,
            });

            if (`${response?.errorcode}` !== '0') this.restoreTitle(pin.webpagepinned_keyno, newTitle, oldTitle);
        } catch {
            this.restoreTitle(pin.webpagepinned_keyno, newTitle, oldTitle);
        } finally {
            this.titleUpdates.delete(pin.webpagepinned_keyno);
        }
    }

    /**
     * Retrieves the current user's persisted pins.
     *
     * @datatask 3263
     * @datataskName sp_ws_p2_webpagepinned_list
     */
    private getPinnedStates(): Promise<PinnedState[]> {
        return this.datatask.Post(3263);
    }

    /**
     * Creates a pin or updates an existing pin when its key is supplied.
     *
     * @datatask 3261
     * @datataskName sp_ws_p2_webpagepinned_pin
     */
    private async savePinnedState(params: SavePinnedStateParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3261, params))[0];
    }

    /**
     * Deletes a persisted pin.
     *
     * @datatask 3262
     * @datataskName sp_ws_p2_webpagepinned_unpin
     */
    private async deletePinnedState(params: DeletePinnedStateParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3262, params))[0];
    }

    private restoreTitle(id: string, attemptedTitle: string, oldTitle: string): void {
        this.setPins(this.pins$.value.map((item) => (item.webpagepinned_keyno === id && item.pinned_name === attemptedTitle ? { ...item, pinned_name: oldTitle } : item)));
    }

    private setPins(pins: PinnedState[]): void {
        this.pins$.next(pins);
        this.history.setProtectedPaths(pins.map((pin) => pin.pinned_url));
    }
}

/** A user-persisted shortcut to an application URL. */
export interface PinnedState {
    /** User-visible pin title. */
    pinned_name: string;
    /** Relative application URL opened by the pin. */
    pinned_url: string;
    /** Backend identifier used when updating or removing the pin. */
    webpagepinned_keyno: string;
}

/** Payload accepted by the pin save datatask. */
export interface SavePinnedStateParams {
    pinned_url: string;
    pinned_name: string;
    webpagepinned_keyno?: string;
}

/** Payload accepted by the pin deletion datatask. */
export interface DeletePinnedStateParams {
    webpagepinned_keyno: string;
}
